import { useCallback, useMemo } from 'react'

import { OnCareOptionModalOpenCloseProps } from '@lyrahealth-inc/shared-app-logic/src/features/searchForCare/constants'

import { useGetAuroraCareOptionsModalContent } from './useGetCareOptionsModalContent'
import { AuroraCareOption } from '../careOptionsMetadata/auroraCareOptionsMetadata'
import { ProgramDetailsContent } from '../organisms/programDetailsModal/ProgramDetailsModal'
import { CareOptionAvailabilityInfo } from '../utils/careOptions/types'
import { getAvailableCareOptions } from '../utils/careOptions/utils'

export interface CareOptionsGridPressHandlers {
  onSelfTriagePress: () => void
  onChildTriagePress: () => void
  onCoachingPress: () => void
  onParentingCoachingPress: () => void
  onCouplesTherapyPress: () => void
  onFamilyTherapyPress: () => void
  onMedicationConsultationPress: () => void
  onOnsiteTherapyPress: () => void
}

interface UseCareOptionsGridPropsArgs {
  careOptionAvailabilityInfo: CareOptionAvailabilityInfo
  /** Per-option navigation handlers used to build `onCareOptionPress`. Provide this OR `onCareOptionPress`. */
  pressHandlers?: CareOptionsGridPressHandlers
  /**
   * Pre-built press handler that overrides `pressHandlers` when provided. Lets callers that already
   * own a unified care-option handler (e.g. Smart Search's `useAuroraCareOptionPress`) reuse it
   * instead of supplying the granular `pressHandlers`.
   */
  onCareOptionPress?: (careOption: AuroraCareOption) => void
  isDirectAccessMedsSupported: boolean
  isBlendedCareMedsAvailable?: boolean
  onContinuePress?: () => void
  customerName?: string
  /** Fired when a care option's program-details modal opens (for analytics). Same shape as v2. */
  onCareOptionModalOpen?: (props: OnCareOptionModalOpenCloseProps) => void
  /** Fired when a care option's program-details modal closes (for analytics). Same shape as v2. */
  onCareOptionModalClose?: (props: OnCareOptionModalOpenCloseProps) => void
  /** Member age, included in the modal open/close tracking payload. */
  userAge?: number
  /** Care option whose modal should auto-open (microsite deep link). */
  selectedCareOption?: AuroraCareOption
  /** Clears the deep-link source once the targeted modal is opened/closed. */
  resetSelectedCareOption?: () => void
}

/**
 * Assembles the props for `CareOptionsGrid` (Forked Triage v3) from a customer's availability
 * info and the app's navigation handlers. Shared by the web/mobile Forked Triage containers and the
 * Smart Search Forked Triage section so the care-option → action mapping lives in one place.
 */
export const useCareOptionsGridProps = ({
  careOptionAvailabilityInfo,
  pressHandlers,
  onCareOptionPress: onCareOptionPressOverride,
  isDirectAccessMedsSupported,
  isBlendedCareMedsAvailable,
  onContinuePress,
  customerName,
  onCareOptionModalOpen,
  onCareOptionModalClose,
  userAge,
  selectedCareOption,
  resetSelectedCareOption,
}: UseCareOptionsGridPropsArgs) => {
  const modalContentByOption = useGetAuroraCareOptionsModalContent({
    isDirectAccessMedsSupported,
    isBlendedCareMedsAvailable,
    onContinuePress,
    onCareOptionModalOpen,
    onCareOptionModalClose,
    userAge,
    selectedCareOption,
    resetSelectedCareOption,
  }) as Partial<Record<AuroraCareOption, ProgramDetailsContent>>

  const careOptions = useMemo(() => getAvailableCareOptions(careOptionAvailabilityInfo), [careOptionAvailabilityInfo])

  const onCareOptionPressFromHandlers = useCallback(
    (careOption: AuroraCareOption) => {
      if (!pressHandlers) return
      const pressByOption: Partial<Record<AuroraCareOption, () => void>> = {
        [AuroraCareOption.SELF_TRIAGE]: pressHandlers.onSelfTriagePress,
        [AuroraCareOption.CHILD_TRIAGE]: pressHandlers.onChildTriagePress,
        [AuroraCareOption.MENTAL_HEALTH_COACHING]: pressHandlers.onCoachingPress,
        [AuroraCareOption.PARENTING_COACHING]: pressHandlers.onParentingCoachingPress,
        [AuroraCareOption.COUPLES_THERAPY]: pressHandlers.onCouplesTherapyPress,
        [AuroraCareOption.FAMILY_THERAPY]: pressHandlers.onFamilyTherapyPress,
        [AuroraCareOption.MEDICATION_CONSULTATION]: pressHandlers.onMedicationConsultationPress,
        [AuroraCareOption.ONSITE_THERAPY]: pressHandlers.onOnsiteTherapyPress,
      }
      pressByOption[careOption]?.()
    },
    [pressHandlers],
  )

  const onCareOptionPress = onCareOptionPressOverride ?? onCareOptionPressFromHandlers

  const formattedMessageValuesByOption = useMemo(
    () => ({ [AuroraCareOption.ONSITE_THERAPY]: { company: customerName } }),
    [customerName],
  )

  return useMemo(
    () => ({ careOptions, onCareOptionPress, modalContentByOption, formattedMessageValuesByOption }),
    [careOptions, onCareOptionPress, modalContentByOption, formattedMessageValuesByOption],
  )
}
